import React, { useEffect, useState } from 'react';
import axios from '../../utils/axiosConfig';
import { widgetUrlForBot } from '../../utils/publicUrls';
import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Card, 
  CardContent, 
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Slider,
  Switch,
  FormControlLabel,
  Divider,
  Alert,
  Snackbar,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Palette as PaletteIcon,
  Preview as PreviewIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  Business as BusinessIcon,
  Chat as ChatIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';

const WidgetPreview = () => {
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [availableBots, setAvailableBots] = useState([]);
  const [selectedBot, setSelectedBot] = useState('');
  const [widgetConfig, setWidgetConfig] = useState({
    primaryColor: '#2b6cb0',
    secondaryColor: '#111827',
    textColor: '#ffffff',
    backgroundColor: '#ffffff',
    borderRadius: 12,
    fontSize: 14,
    showAvatar: true,
    showTypingIndicator: true,
    position: 'bottom-right',
    width: 350,
    height: 500,
    welcomeMessage: 'Hallo! Wie kann ich Ihnen helfen?',
    placeholder: 'Schreiben Sie eine Nachricht...',
    buttonText: 'Chat starten',
    companyName: '',
    systemPrompt: 'Du bist ein hilfsreicher KI-Assistent.'
  });
  const [previewUrl, setPreviewUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    loadCompanies();
    loadBots();
  }, []);

  const loadCompanies = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL || 'https://backend.mtsdemo.de'}/api/companies/`);
      setCompanies(res.data);
      if (res.data.length > 0) {
        setSelectedCompany(res.data[0].id);
        loadCompanyConfig(res.data[0].id);
      }
    } catch (error) {
      console.error('Fehler beim Laden der Firmen:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Fehler beim Laden der Firmen';
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
    }
  };

  const loadBots = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL || 'https://backend.mtsdemo.de'}/api/bots/`);
      setAvailableBots(res.data);
      if (res.data.length > 0) {
        setSelectedBot(res.data[0].id);
        generatePreviewUrl(res.data[0].id);
      }
    } catch (error) {
      console.error('Fehler beim Laden der Bots:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Fehler beim Laden der Bots';
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
    }
  };

  const loadCompanyConfig = async (companyId) => {
    try {
      setLoading(true);
      const res = await axios.get(`${process.env.REACT_APP_API_URL || 'https://backend.mtsdemo.de'}/api/companies/${companyId}`);
      const company = res.data;
      
      setWidgetConfig(prev => ({
        ...prev,
        companyName: company.display_name,
        systemPrompt: company.system_prompt,
        primaryColor: company.settings?.primaryColor || '#2b6cb0',
        secondaryColor: company.settings?.secondaryColor || '#111827',
        textColor: company.settings?.textColor || '#ffffff',
        backgroundColor: company.settings?.backgroundColor || '#ffffff',
        borderRadius: company.settings?.borderRadius || 12,
        fontSize: company.settings?.fontSize || 14,
        showAvatar: company.settings?.showAvatar !== false,
        showTypingIndicator: company.settings?.showTypingIndicator !== false,
        position: company.settings?.position || 'bottom-right',
        width: company.settings?.width || 350,
        height: company.settings?.height || 500,
        welcomeMessage: company.settings?.welcomeMessage || 'Hallo! Wie kann ich helfen?',
        placeholder: company.settings?.placeholder || 'Schreiben Sie eine Nachricht...',
        buttonText: company.settings?.buttonText || 'Chat starten'
      }));
      
      generatePreviewUrl(company.slug);
    } catch (error) {
      console.error('Fehler beim Laden der Firmenkonfiguration:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Fehler beim Laden der Konfiguration';
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const generatePreviewUrl = (botId) => {
    const url = widgetUrlForBot(botId);
    setPreviewUrl(url);
    setShowPreview(true);
  };

  const handleCompanyChange = (companyId) => {
    setSelectedCompany(companyId);
    const company = companies.find(c => c.id === companyId);
    if (company) {
      loadCompanyConfig(companyId);
    }
  };

  const handleBotChange = (botId) => {
    setSelectedBot(botId);
    generatePreviewUrl(botId);
  };

  const handleConfigChange = (field, value) => {
    setWidgetConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const saveConfig = async () => {
    if (!selectedCompany) return;
    
    try {
      setSaving(true);
      const settings = {
        primaryColor: widgetConfig.primaryColor,
        secondaryColor: widgetConfig.secondaryColor,
        textColor: widgetConfig.textColor,
        backgroundColor: widgetConfig.backgroundColor,
        borderRadius: widgetConfig.borderRadius,
        fontSize: widgetConfig.fontSize,
        showAvatar: widgetConfig.showAvatar,
        showTypingIndicator: widgetConfig.showTypingIndicator,
        position: widgetConfig.position,
        width: widgetConfig.width,
        height: widgetConfig.height,
        welcomeMessage: widgetConfig.welcomeMessage,
        placeholder: widgetConfig.placeholder,
        buttonText: widgetConfig.buttonText
      };
      
      await axios.put(`${process.env.REACT_APP_API_URL || 'https://backend.mtsdemo.de'}/api/companies/${selectedCompany}`, {
        settings: settings,
        system_prompt: widgetConfig.systemPrompt
      });
      
      setSnackbar({ open: true, message: 'Konfiguration erfolgreich gespeichert', severity: 'success' });
    } catch (error) {
      console.error('Fehler beim Speichern:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Fehler beim Speichern';
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const resetConfig = () => {
    if (selectedCompany) {
      loadCompanyConfig(selectedCompany);
    }
  };

  useEffect(() => {
    loadCompanies();
  }, []);

  const selectedCompanyData = companies.find(c => c.id === selectedCompany);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <PreviewIcon sx={{ fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h4">Widget Vorschau</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button 
            variant="outlined" 
            startIcon={<RefreshIcon />}
            onClick={resetConfig}
            disabled={loading}
          >
            Zurücksetzen
          </Button>
          <Button 
            variant="contained" 
            startIcon={<SaveIcon />}
            onClick={saveConfig}
            disabled={saving || !selectedCompany}
          >
            {saving ? 'Speichern...' : 'Speichern'}
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Konfiguration */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <SettingsIcon color="primary" />
                <Typography variant="h6">Widget Konfiguration</Typography>
              </Box>
              
              {/* Bot-Auswahl */}
              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel>Bot auswählen</InputLabel>
                <Select
                  value={selectedBot}
                  onChange={(e) => handleBotChange(e.target.value)}
                  label="Bot auswählen"
                  disabled={loading}
                >
                  {availableBots.map((bot) => (
                    <MenuItem key={bot.id} value={bot.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ChatIcon fontSize="small" />
                        <Box>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {bot.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {bot.description || 'Keine Beschreibung'}
                          </Typography>
                        </Box>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Firmenauswahl */}
              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel>Firma auswählen</InputLabel>
                <Select
                  value={selectedCompany}
                  onChange={(e) => handleCompanyChange(e.target.value)}
                  label="Firma auswählen"
                >
                  {companies.map((company) => (
                    <MenuItem key={company.id} value={company.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <BusinessIcon />
                        <Box>
                          <Typography variant="body1">{company.display_name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {company.slug}
                          </Typography>
                        </Box>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {selectedCompanyData && (
                <>
                  <Divider sx={{ my: 2 }} />
                  
                  {/* Farben */}
                  <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PaletteIcon color="primary" />
                        <Typography>Farben & Design</Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <TextField
                            label="Primärfarbe"
                            type="color"
                            value={widgetConfig.primaryColor}
                            onChange={(e) => handleConfigChange('primaryColor', e.target.value)}
                            fullWidth
                            InputProps={{ sx: { height: 56 } }}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <TextField
                            label="Sekundärfarbe"
                            type="color"
                            value={widgetConfig.secondaryColor}
                            onChange={(e) => handleConfigChange('secondaryColor', e.target.value)}
                            fullWidth
                            InputProps={{ sx: { height: 56 } }}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <TextField
                            label="Textfarbe"
                            type="color"
                            value={widgetConfig.textColor}
                            onChange={(e) => handleConfigChange('textColor', e.target.value)}
                            fullWidth
                            InputProps={{ sx: { height: 56 } }}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <TextField
                            label="Hintergrundfarbe"
                            type="color"
                            value={widgetConfig.backgroundColor}
                            onChange={(e) => handleConfigChange('backgroundColor', e.target.value)}
                            fullWidth
                            InputProps={{ sx: { height: 56 } }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Typography gutterBottom>Border Radius: {widgetConfig.borderRadius}px</Typography>
                          <Slider
                            value={widgetConfig.borderRadius}
                            onChange={(e, value) => handleConfigChange('borderRadius', value)}
                            min={0}
                            max={30}
                            step={1}
                            marks
                            valueLabelDisplay="auto"
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Typography gutterBottom>Schriftgröße: {widgetConfig.fontSize}px</Typography>
                          <Slider
                            value={widgetConfig.fontSize}
                            onChange={(e, value) => handleConfigChange('fontSize', value)}
                            min={10}
                            max={20}
                            step={1}
                            marks
                            valueLabelDisplay="auto"
                          />
                        </Grid>
                      </Grid>
                    </AccordionDetails>
                  </Accordion>

                  {/* Größe & Position */}
                  <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography>Größe & Position</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <TextField
                            label="Breite (px)"
                            type="number"
                            value={widgetConfig.width}
                            onChange={(e) => handleConfigChange('width', parseInt(e.target.value))}
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <TextField
                            label="Höhe (px)"
                            type="number"
                            value={widgetConfig.height}
                            onChange={(e) => handleConfigChange('height', parseInt(e.target.value))}
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <FormControl fullWidth>
                            <InputLabel>Position</InputLabel>
                            <Select
                              value={widgetConfig.position}
                              onChange={(e) => handleConfigChange('position', e.target.value)}
                              label="Position"
                            >
                              <MenuItem value="bottom-right">Unten rechts</MenuItem>
                              <MenuItem value="bottom-left">Unten links</MenuItem>
                              <MenuItem value="top-right">Oben rechts</MenuItem>
                              <MenuItem value="top-left">Oben links</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                      </Grid>
                    </AccordionDetails>
                  </Accordion>

                  {/* Nachrichten */}
                  <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ChatIcon color="primary" />
                        <Typography>Nachrichten & Texte</Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <TextField
                            label="Willkommensnachricht"
                            value={widgetConfig.welcomeMessage}
                            onChange={(e) => handleConfigChange('welcomeMessage', e.target.value)}
                            fullWidth
                            multiline
                            rows={2}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            label="Platzhalter-Text"
                            value={widgetConfig.placeholder}
                            onChange={(e) => handleConfigChange('placeholder', e.target.value)}
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            label="Button-Text"
                            value={widgetConfig.buttonText}
                            onChange={(e) => handleConfigChange('buttonText', e.target.value)}
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            label="System-Prompt"
                            value={widgetConfig.systemPrompt}
                            onChange={(e) => handleConfigChange('systemPrompt', e.target.value)}
                            fullWidth
                            multiline
                            rows={3}
                            helperText="Anweisungen für das KI-Modell"
                          />
                        </Grid>
                      </Grid>
                    </AccordionDetails>
                  </Accordion>

                  {/* Optionen */}
                  <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography>Optionen</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={widgetConfig.showAvatar}
                              onChange={(e) => handleConfigChange('showAvatar', e.target.checked)}
                            />
                          }
                          label="Avatar anzeigen"
                        />
                        <FormControlLabel
                          control={
                            <Switch
                              checked={widgetConfig.showTypingIndicator}
                              onChange={(e) => handleConfigChange('showTypingIndicator', e.target.checked)}
                            />
                          }
                          label="Tipp-Indikator anzeigen"
                        />
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Vorschau */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <PreviewIcon color="primary" />
                <Typography variant="h6">Live Vorschau</Typography>
              </Box>

              {selectedCompanyData ? (
                <Box>
                  <Box sx={{ mb: 2 }}>
                    <Chip 
                      label={selectedCompanyData.display_name} 
                      color="primary" 
                      sx={{ mb: 1 }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      Slug: {selectedCompanyData.slug}
                    </Typography>
                  </Box>

                  <Paper 
                    sx={{ 
                      p: 2, 
                      backgroundColor: 'grey.100',
                      borderRadius: 2,
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      minHeight: 400,
                      position: 'relative'
                    }}
                  >
                    {showPreview && previewUrl ? (
                      <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
                        <iframe
                          title="Widget Vorschau"
                          src={previewUrl}
                          style={{
                            width: '100%',
                            height: '500px',
                            border: 'none',
                            borderRadius: `${widgetConfig.borderRadius}px`,
                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                          }}
                        />
                        <Box sx={{ 
                          position: 'absolute', 
                          top: 8, 
                          right: 8, 
                          display: 'flex', 
                          gap: 1 
                        }}>
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() => window.open(previewUrl, '_blank')}
                            startIcon={<PreviewIcon />}
                          >
                            In neuem Tab öffnen
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => setShowPreview(false)}
                          >
                            Vorschau schließen
                          </Button>
                        </Box>
                      </Box>
                    ) : (
                      <Box sx={{ textAlign: 'center', color: 'text.secondary' }}>
                        <PreviewIcon sx={{ fontSize: 48, mb: 2 }} />
                        <Typography variant="h6" gutterBottom>
                          Widget-Vorschau
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 2 }}>
                          Wählen Sie einen Bot aus, um die Vorschau zu generieren
                        </Typography>
                        {selectedBot && (
                          <Button
                            variant="contained"
                            onClick={() => generatePreviewUrl(selectedBot)}
                            startIcon={<PreviewIcon />}
                          >
                            Vorschau generieren
                          </Button>
                        )}
                      </Box>
                    )}
                  </Paper>

                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Einbettungscode:
                    </Typography>
                    <TextField
                      value={`<iframe src="${widgetUrlForBot(selectedBot || 1)}" title="KiChatbot" style="width:400px;height:600px;border:0" />`}
                      fullWidth
                      size="small"
                      InputProps={{ readOnly: true }}
                      sx={{ fontFamily: 'monospace' }}
                    />
                  </Box>
                </Box>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <BusinessIcon sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    Keine Firma ausgewählt
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Wählen Sie eine Firma aus, um das Widget zu konfigurieren
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default WidgetPreview;